import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  Briefcase,
  FileText,
  Receipt,
  PenTool,
  MessageSquare,
  Calendar,
  Clock,
  AlertCircle,
  ArrowRight
} from 'lucide-react'
import api from '../config/api'
import { useAuth } from '../hooks/useAuth'
import '../styles/Dashboard.css'

function Dashboard() {
  const { client } = useAuth()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    fetchDashboard()
  }, [])

  const fetchDashboard = async () => {
    try {
      const response = await api.get('/dashboard')
      setData(response.data.data)
    } catch (err) {
      setError('Failed to load dashboard data')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner" />
        <p>Loading dashboard...</p>
      </div>
    )
  }

  const stats = [
    {
      label: 'Active Matters',
      value: data?.counts?.matters || 0,
      icon: Briefcase,
      link: '/matters',
      color: 'blue'
    },
    {
      label: 'Documents',
      value: data?.counts?.documents || 0,
      icon: FileText,
      link: '/documents',
      color: 'green'
    },
    {
      label: 'Pending Signatures',
      value: data?.counts?.pendingSignatures || 0,
      icon: PenTool,
      link: '/documents',
      color: 'orange',
      highlight: data?.counts?.pendingSignatures > 0
    },
    {
      label: 'Unpaid Invoices',
      value: data?.counts?.unpaidInvoices || 0,
      icon: Receipt,
      link: '/invoices',
      color: 'red',
      highlight: data?.counts?.unpaidInvoices > 0
    }
  ]

  return (
    <div className="portal-dashboard">
      <header className="dashboard-header">
        <div>
          <h1>Welcome back, {client?.firstName}</h1>
          <p>Here's an overview of your account</p>
        </div>
      </header>

      {error && (
        <div className="alert alert-error">
          <AlertCircle size={18} />
          <span>{error}</span>
        </div>
      )}

      {/* Stats Grid */}
      <div className="stats-grid">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <Link
              to={stat.link}
              className={`stat-card ${stat.color} ${stat.highlight ? 'highlight' : ''}`}
            >
              <div className="stat-icon">
                <stat.icon size={24} />
              </div>
              <div className="stat-content">
                <span className="stat-value">{stat.value}</span>
                <span className="stat-label">{stat.label}</span>
              </div>
              <ArrowRight size={20} className="stat-arrow" />
            </Link>
          </motion.div>
        ))}
      </div>

      {/* Quick Actions */}
      <section className="dashboard-section">
        <h2>Quick Actions</h2>
        <div className="quick-actions">
          <Link to="/messages" className="quick-action">
            <MessageSquare size={20} />
            <span>Send Message</span>
            {data?.counts?.unreadMessages > 0 && (
              <span className="badge">{data.counts.unreadMessages}</span>
            )}
          </Link>
          <Link to="/documents" className="quick-action">
            <FileText size={20} />
            <span>Upload Document</span>
          </Link>
          <Link to="/invoices" className="quick-action">
            <Receipt size={20} />
            <span>View Invoices</span>
          </Link>
        </div>
      </section>

      {/* Pending Signatures Alert */}
      {data?.counts?.pendingSignatures > 0 && (
        <motion.section
          className="alert-section"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <div className="alert-card signature-alert">
            <div className="alert-icon">
              <PenTool size={24} />
            </div>
            <div className="alert-content">
              <h3>Documents Awaiting Your Signature</h3>
              <p>You have {data.counts.pendingSignatures} document(s) that require your signature.</p>
            </div>
            <Link to="/documents" className="btn btn-primary">
              Review & Sign
            </Link>
          </div>
        </motion.section>
      )}

      {/* Recent Activity */}
      {data?.recentActivity?.length > 0 && (
        <section className="dashboard-section">
          <h2>Recent Activity</h2>
          <div className="activity-list">
            {data.recentActivity.map((activity, index) => (
              <div key={activity.id || index} className="activity-item">
                <div className="activity-icon">
                  <Clock size={16} />
                </div>
                <div className="activity-content">
                  <span className="activity-action">{formatAction(activity.action)}</span>
                  {activity.resourceName && (
                    <span className="activity-resource">{activity.resourceName}</span>
                  )}
                </div>
                <span className="activity-time">
                  {formatTime(activity.timestamp)}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  )
}

function formatAction(action) {
  const actionLabels = {
    login: 'Signed in',
    document_view: 'Viewed document',
    document_download: 'Downloaded document',
    document_upload: 'Uploaded document',
    matter_view: 'Viewed matter',
    invoice_view: 'Viewed invoice',
    message_sent: 'Sent message',
    signature_completed: 'Signed document',
    profile_update: 'Updated profile'
  }
  return actionLabels[action] || action
}

function formatTime(timestamp) {
  if (!timestamp) return ''

  let date
  // Handle Firestore Timestamp format (serialized as {_seconds, _nanoseconds})
  if (timestamp._seconds) {
    date = new Date(timestamp._seconds * 1000)
  } else if (timestamp.seconds) {
    date = new Date(timestamp.seconds * 1000)
  } else if (timestamp.toDate) {
    date = timestamp.toDate()
  } else {
    date = new Date(timestamp)
  }

  // Check for invalid date
  if (isNaN(date.getTime())) return ''

  const now = new Date()
  const diff = now - date

  if (diff < 60000) return 'Just now'
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`
  return date.toLocaleDateString()
}

export default Dashboard
